/**
 * Paginação SPA (page-break) — coleta de config e HTML do rodapé no export/preview.
 * @package gepetto-frontend
 */

export const PAGE_BREAK_NAV_STYLES = new Set([
  'simple-text',
  'simple-arrows',
  'arrows-fraction',
  'arrows-numbers',
]);

/**
 * @param {string} raw
 * @returns {'simple-text'|'simple-arrows'|'arrows-fraction'|'arrows-numbers'}
 */
export function normalizePageBreakNavStyle(raw) {
  const v = String(raw || 'simple-text').trim();
  return PAGE_BREAK_NAV_STYLES.has(v) ? v : 'simple-text';
}

/**
 * Varre linhas/colunas/subgrids na ordem do documento.
 * @param {Array<object>|null|undefined} rows
 * @returns {Array<object>}
 */
export function collectPageBreakWidgetsFromRows(rows) {
  /** @type {Array<object>} */
  const out = [];

  const visitCol = (col) => {
    for (const w of col?.widgets || []) {
      if (w?.type === 'page-break') out.push(w);
    }
    for (const nr of col?.nestedRows || []) {
      for (const c2 of nr?.columns || []) visitCol(c2);
    }
  };

  for (const row of rows || []) {
    for (const col of row?.columns || []) visitCol(col);
  }
  return out;
}

/**
 * Estilo/cores do 1.º page-break; redirect da última página no último page-break.
 * @param {Array<object>|null|undefined} rows
 * @returns {Record<string, string>}
 */
export function collectPageBreakExportConfig(rows) {
  const breaks = collectPageBreakWidgetsFromRows(rows);
  const first = breaks[0]?.content && typeof breaks[0].content === 'object' ? breaks[0].content : {};
  const last = breaks.length
    ? (breaks[breaks.length - 1]?.content && typeof breaks[breaks.length - 1].content === 'object'
      ? breaks[breaks.length - 1].content
      : {})
    : {};

  return {
    navStyle: normalizePageBreakNavStyle(first.navStyle),
    btnBgColor: String(first.btnBgColor || '#3b82f6').trim() || '#3b82f6',
    btnTextColor: String(first.btnTextColor || '#ffffff').trim() || '#ffffff',
    textColor: String(first.textColor || '#64748b').trim() || '#64748b',
    lastPageAction: String(last.lastPageAction || first.lastPageAction || 'none').toLowerCase(),
    lastPageUrl: String(last.lastPageUrl || first.lastPageUrl || '').trim(),
    lastPageLabel: String(last.lastPageLabel || first.lastPageLabel || 'Concluir e voltar ao curso').trim(),
  };
}

/**
 * Gera no máximo `maxInnerSlots` entradas (números + reticências) para paginação compacta.
 * Prev/next ficam fora — total da linha = maxInnerSlots + 2 (máx. 7 botões).
 * @param {number} pageCount
 * @param {number} currentIndex
 * @param {number} [maxInnerSlots=5]
 * @returns {Array<{type:'page',index:number}|{type:'ellipsis'}>}
 */
export function computeCompactPageNumberSlots(pageCount, currentIndex, maxInnerSlots = 5) {
  const total = Math.max(0, Number(pageCount) || 0);
  const cur = Math.min(Math.max(0, Number(currentIndex) || 0), Math.max(0, total - 1));
  const maxSlots = Math.max(3, Number(maxInnerSlots) || 5);

  if (total <= maxSlots) {
    return Array.from({ length: total }, (_, i) => ({ type: 'page', index: i }));
  }

  const last = total - 1;

  /** @type {Array<{type:'page',index:number}|{type:'ellipsis'}>} */
  let items = [];

  // Início: 1  2  3  4  …  N
  if (cur <= 3) {
    items = [];
    for (let i = 0; i <= Math.min(3, last); i++) {
      items.push({ type: 'page', index: i });
    }
    if (last > 4) {
      items.push({ type: 'ellipsis' }, { type: 'page', index: last });
    }
    return items.slice(0, maxSlots);
  }

  // Final: 1  …  N-2  N-1  N
  if (cur >= last - 2) {
    items = [{ type: 'page', index: 0 }];
    if (last > 3) items.push({ type: 'ellipsis' });
    for (let i = Math.max(last - 2, 1); i <= last; i++) {
      items.push({ type: 'page', index: i });
    }
    return items.slice(0, maxSlots);
  }

  // Meio: 1  …  cur-1  cur  cur+1  (máx. 5 slots internos)
  items = [{ type: 'page', index: 0 }];
  if (cur - 1 > 1) {
    items.push({ type: 'ellipsis' });
  } else if (cur - 1 === 1) {
    items.push({ type: 'page', index: 1 });
  }
  items.push(
    { type: 'page', index: cur - 1 },
    { type: 'page', index: cur },
    { type: 'page', index: cur + 1 },
  );
  if (items.length < maxSlots && cur + 1 < last - 1) {
    items.push({ type: 'ellipsis' }, { type: 'page', index: last });
  }
  return items.slice(0, maxSlots);
}

/**
 * @param {Array<{type:'page',index:number}|{type:'ellipsis'}>} slots
 * @param {number} currentIndex
 * @param {(active:boolean)=>string} numStyle
 * @returns {string}
 */
export function renderCompactPageNumberSlotsHtml(slots, currentIndex, numStyle) {
  return slots.map((slot) => {
    if (slot.type === 'ellipsis') {
      return '<span class="madri-spa-pagination__ellipsis" aria-hidden="true">…</span>';
    }
    const active = slot.index === currentIndex;
    return `<span class="madri-spa-pagination__num" role="button" tabindex="0" onclick="madriGoToPage(${slot.index})" style="${numStyle(active)}">${slot.index + 1}</span>`;
  }).join('');
}

/**
 * HTML do rodapé de paginação (Visualizar / export Moodle).
 * @param {object} opts
 * @returns {string}
 */
export function buildSpaPaginationContentHtml(opts) {
  const {
    navStyle,
    idx,
    pageCount,
    prev,
    next,
    pageLabel,
    btnBg,
    btnText,
    textColor,
    lastPageAction,
    lastPageUrlEscaped,
    lastPageLabel,
  } = opts;

  const isFirst = idx <= 0;
  const isLast = idx >= pageCount - 1;
  const style = normalizePageBreakNavStyle(navStyle);

  const btnBase = 'padding:8px 16px;border:none;border-radius:4px;font-size:14px;font-weight:500;margin:0 4px;display:inline-block;transition:opacity 0.2s;font-family:inherit;box-sizing:border-box;cursor:pointer;';
  const btnPrevStyle = `${btnBase}background:${isFirst ? '#f1f5f9' : btnBg};color:${isFirst ? '#94a3b8' : btnText};cursor:${isFirst ? 'not-allowed' : 'pointer'};`;
  const btnNextStyle = `${btnBase}background:${isLast ? '#f1f5f9' : btnBg};color:${isLast ? '#94a3b8' : btnText};cursor:${isLast ? 'not-allowed' : 'pointer'};`;
  const btnLinkStyle = `padding:8px 16px;background:${btnBg};color:${btnText};border:none;border-radius:4px;cursor:pointer;font-size:14px;font-weight:500;text-decoration:none;display:inline-block;margin:0 4px;font-family:inherit;box-sizing:border-box;`;
  const badgeStyle = `width:36px;height:36px;min-width:36px;display:inline-flex;align-items:center;justify-content:center;border-radius:4px;background:transparent;color:${btnBg};border:1px solid ${btnBg};opacity:0.6;font-size:14px;font-weight:500;margin:0 4px;box-sizing:border-box;`;
  const numStyle = (active) => `width:36px;height:36px;min-width:36px;display:inline-flex;align-items:center;justify-content:center;border-radius:4px;background:${active ? btnBg : 'transparent'};color:${active ? btnText : btnBg};border:${active ? 'none' : `1px solid ${btnBg}`};opacity:${active ? '1' : '0.6'};cursor:pointer;font-weight:500;font-size:14px;box-shadow:${active ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'};box-sizing:border-box;`;

  const nextOrRedirect = isLast && lastPageAction === 'redirect' && lastPageUrlEscaped
    ? `<button type="button" class="madri-spa-pagination__redirect" data-madri-redirect="${lastPageUrlEscaped}" onclick="var u=this.getAttribute('data-madri-redirect');if(u){madriGoRedirect(u);}" style="${btnLinkStyle}">${lastPageLabel}</button>`
    : `<button type="button" class="madri-spa-pagination__next" onclick="madriGoToPage(${idx + 1})" style="${btnNextStyle}" ${isLast ? 'disabled' : ''}>${style === 'simple-text' ? `${next} &raquo;` : '&rsaquo;'}</button>`;

  if (style === 'simple-arrows') {
    return `
            <button type="button" class="madri-spa-pagination__prev" onclick="madriGoToPage(${idx - 1})" style="${btnPrevStyle}" ${isFirst ? 'disabled' : ''}>&lsaquo; ${prev}</button>
            <span class="madri-spa-pagination__page-badge" style="${badgeStyle}">${idx + 1}</span>
            ${nextOrRedirect}
          `;
  }

  if (style === 'arrows-fraction') {
    return `
            <button type="button" class="madri-spa-pagination__prev" onclick="madriGoToPage(${idx - 1})" style="${btnPrevStyle}" ${isFirst ? 'disabled' : ''}>&lsaquo;</button>
            <span class="madri-spa-pagination__fraction" style="color:${textColor};font-size:14px;font-weight:bold;margin:0 8px;letter-spacing:2px;">${idx + 1}/${pageCount}</span>
            ${nextOrRedirect}
          `;
  }

  if (style === 'arrows-numbers') {
    const fullNums = Array.from({ length: pageCount }, (_, i) => (
      `<span class="madri-spa-pagination__num" role="button" tabindex="0" onclick="madriGoToPage(${i})" style="${numStyle(i === idx)}">${i + 1}</span>`
    )).join('');
    const compactSlots = computeCompactPageNumberSlots(pageCount, idx, 5);
    const compactNums = renderCompactPageNumberSlotsHtml(compactSlots, idx, numStyle);
    return `
            <button type="button" class="madri-spa-pagination__prev" onclick="madriGoToPage(${idx - 1})" style="${btnPrevStyle}" ${isFirst ? 'disabled' : ''}>&lsaquo;</button>
            <div class="madri-spa-pagination__nums madri-spa-pagination__nums--desktop">${fullNums}</div>
            <div class="madri-spa-pagination__nums madri-spa-pagination__nums--mobile">${compactNums}</div>
            ${nextOrRedirect}
          `;
  }

  // simple-text (default)
  return `
            <button type="button" class="madri-spa-pagination__prev" onclick="madriGoToPage(${idx - 1})" style="${btnPrevStyle}" ${isFirst ? 'disabled' : ''}>&laquo; ${prev}</button>
            <span class="madri-spa-pagination__label" style="color:${textColor};font-size:14px;font-weight:bold;margin:0 8px;display:inline-flex;align-items:center;">${pageLabel || 'Página'} ${idx + 1} de ${pageCount}</span>
            ${nextOrRedirect}
          `;
}
